import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useCustomer } from "./CustomerContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { customer } = useCustomer();
  const skipSync = useRef(false);
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("td_cart")) || [];
    } catch {
      return [];
    }
  });

  // Hydrate the server-saved cart when a customer logs in
  useEffect(() => {
    if (!customer) return;
    skipSync.current = true;
    api
      .get("/cart")
      .then((r) => {
        setItems(r.data.items.map((i) => ({ product: i.product, qty: i.qty })));
      })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => { skipSync.current = false; }, 300);
      });
  }, [customer?.email]);

  // Persist locally, and to the server (debounced) when logged in
  const pendingRef = useRef(null);
  useEffect(() => {
    localStorage.setItem("td_cart", JSON.stringify(items));
    if (!customer || skipSync.current) return;
    pendingRef.current = items;
    const t = setTimeout(() => {
      pendingRef.current = null;
      api
        .put("/cart", { items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })) })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [items, customer?.email]);

  // Flush any pending cart sync if the app unmounts before the debounce fires
  useEffect(() => {
    const flush = () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      api
        .put("/cart", { items: pending.map((i) => ({ product_id: i.product.id, quantity: i.qty })) })
        .catch(() => {});
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, []);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: Math.min(i.qty + qty, 20) } : i
        );
      }
      return [...prev, { product, qty }];
    });
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.product.id !== id));
  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.product.id !== id) : prev.map((i) => (i.product.id === id ? { ...i, qty } : i))
    );
  const clear = () => setItems([]);

  const value = useMemo(
    () => ({
      items,
      add,
      remove,
      setQty,
      clear,
      count: items.reduce((s, i) => s + i.qty, 0),
      subtotal: items.reduce((s, i) => s + i.product.price * i.qty, 0),
    }),
    [items]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
